<template>
  <div id="tools-page">
    <div class="header">
      <h2 class="page-title">Available Tools</h2>
      <div class="search-container">
        <v-text-field
          v-model="searchQuery"
          placeholder="Search tools..."
          prepend-icon="mdi-magnify"
          clearable
          single-line
          hide-details
          class="search-field"
        />
      </div>
    </div>
    <div v-if="loading" class="loading-container">
      <v-progress-circular indeterminate color="primary" />
      <p>Loading tools...</p>
    </div>
    <transition-group
      v-else-if="filteredTools.length > 0"
      name="fade-slide"
      tag="div"
      class="tools-container"
    >
      <div
        v-for="(tool, index) in filteredTools"
        :key="tool.id"
        :style="{ '--animation-delay': `${index * 50}ms` }"
        class="tool-wrapper"
      >
        <tool-card :tool="tool" />
      </div>
    </transition-group>
    <div v-else class="no-results">
      <p>No tools found matching your search.</p>
    </div>
  </div>
</template>

<script>
import apiClient from "@/plugins/apiService";
import ToolCard from "@/components/ToolCard.vue";

export default {
  name: "ToolsList",
  components: {
    ToolCard,
  },
  data() {
    return {
      tools: [],
      loading: true,
      searchQuery: "",
    };
  },
  computed: {
    filteredTools() {
      if (!this.searchQuery.trim()) {
        return this.tools;
      }

      const query = this.searchQuery.toLowerCase();
      return this.tools.filter((tool) => {
        return (
          tool.name.toLowerCase().includes(query) ||
          tool.description.toLowerCase().includes(query) ||
          (tool.use_case && tool.use_case.toLowerCase().includes(query)) ||
          (tool.location && tool.location.toLowerCase().includes(query))
        );
      });
    },
  },
  created() {
    this.fetchTools();
  },
  methods: {
    async fetchTools() {
      try {
        this.loading = true;
        const response = await apiClient.get("/tools");
        this.tools = response.data.probono_tools;
      } catch (error) {
        console.error("Error fetching tools:", error);
        this.tools = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
#tools-page {
  padding: 40px;
  flex: 1;
  animation: fadeInPage 0.6s ease-out;
}

@keyframes fadeInPage {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.page-title {
  color: var(--primaryColor);
  margin-bottom: 30px;
  text-align: center;
  animation: slideDown 0.5s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.search-container {
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  animation: slideUp 0.5s ease-out 0.1s both;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.search-field {
  width: 100%;
}

.tools-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 20px;
  animation: fadeInPage 0.6s ease-out;
}

.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: var(--contrastColor);
  font-size: 18px;
  animation: fadeInPage 0.6s ease-out;
}

.tool-wrapper {
  animation: slideInCard 0.4s ease-out backwards;
  animation-delay: var(--animation-delay, 0);
}

@keyframes slideInCard {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Transition group animations */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-slide-move {
  transition: transform 0.3s ease;
}
</style>
